import React, { useCallback, useState } from "react";
import { router } from "@/routes/router";
import { Engagement } from "@/types/engagement";
import { installSharePointWebShim } from "./sharePointWebShim";
import {
  isIndependencePlaceholderValue,
  persistWebArchiveEntityLabels,
} from "@/utils/independence-entity-label";
import { SHAREPOINT_CONFIG_UPDATED_EVENT } from "@/utils/sharepoint-env";
import { WEB_ARCHIVE_ENGAGEMENT_ID, ZipEngagementFs } from "./zipFilesystem";
import logoNameDark from "/logo_name_dark.png";

/** Local-only session marker; must match AuthContext skip logic for getCurrentUser. */
export const WEB_ARCHIVE_ACCESS_TOKEN = "offline-web-archive-token";

const ENGAGEMENTS_KEY = "juggernaut_engagements";

function upsertOfflineEngagement(details: Record<string, unknown> | null, fyYear: string) {
  const raw = localStorage.getItem(ENGAGEMENTS_KEY);
  const list: Engagement[] = raw ? JSON.parse(raw) : [];
  const filtered = list.filter((e) => e.id !== WEB_ARCHIVE_ENGAGEMENT_ID);
  const eng: Engagement = {
    id: WEB_ARCHIVE_ENGAGEMENT_ID,
    clientId: String(details?.client_id || details?.clientId || "offline-client"),
    clientName: String(
      details?.client_name || details?.["Client Name"] || details?.clientName || "Client",
    ),
    engagementName: String(details?.engagement_name || fyYear),
    typeOfEngagement: String(details?.engagement_type || "Financial statement audit"),
    financialReportingFramework: details?.frf as string | undefined,
    reportingDate: undefined,
    financialYear: fyYear,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  filtered.push(eng);
  localStorage.setItem(ENGAGEMENTS_KEY, JSON.stringify(filtered));
  localStorage.setItem("current_engagement_id", WEB_ARCHIVE_ENGAGEMENT_ID);
}

export function WebArchiveGate({ children }: { children: React.ReactNode }) {
  /** ZIP is only kept in memory; each full page load requires choosing the file again. */
  const [ready, setReady] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("No file chosen");
  const [isLoadingZip, setIsLoadingZip] = useState(false);

  const onPickZip = useCallback(async (file: File | null) => {
    if (!file) return;
    setSelectedFileName(file.name || "No file chosen");
    setIsLoadingZip(true);
    try {
      const buf = await file.arrayBuffer();
      const zfs = ZipEngagementFs.fromArrayBuffer(buf);
      const details = zfs.getEngagementDetails();
      let clientName = String(
        details?.client_name || details?.["Client Name"] || details?.clientName || "",
      ).trim();
      if (!clientName || isIndependencePlaceholderValue(clientName)) {
        const fromZipName = (file.name || "").replace(/\.zip$/i, "").trim();
        if (fromZipName && !isIndependencePlaceholderValue(fromZipName)) {
          clientName = fromZipName;
        }
      }
      if (!clientName) clientName = "Client";

      persistWebArchiveEntityLabels(clientName, zfs.fyYear);

      localStorage.setItem(
        "sharepoint_config",
        JSON.stringify({
          TENANT_ID: "offline",
          CLIENT_ID: "offline",
          CLIENT_SECRET: "offline-placeholder-secret",
          SITE_HOSTNAME: "offline.local",
          SITE_PATH: "/",
          DOC_LIBRARY: clientName,
          FY_YEAR: zfs.fyYear,
          USERNAME: "Offline",
        }),
      );
      window.dispatchEvent(new Event(SHAREPOINT_CONFIG_UPDATED_EVENT));

      localStorage.setItem("accessToken", WEB_ARCHIVE_ACCESS_TOKEN);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: "offline-user",
          user_name: "Offline",
          email: "offline@local",
          firm_id: "offline",
          type: "User",
        }),
      );

      upsertOfflineEngagement(details, zfs.fyYear);
      sessionStorage.setItem("web_archive_mode", "1");

      installSharePointWebShim(zfs);
      setReady(true);
      queueMicrotask(() => {
        router.navigate({
          to: "/engagement/$engagementId",
          params: { engagementId: WEB_ARCHIVE_ENGAGEMENT_ID },
        });
      });
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : "Failed to read ZIP archive");
      setIsLoadingZip(false);
    }
  }, []);

  if (import.meta.env.VITE_WEB_ARCHIVE !== "true") {
    return <>{children}</>;
  }

  if (!ready) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-4 py-8 text-white sm:px-6">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(77,163,255,0.16),transparent_45%),radial-gradient(circle_at_80%_10%,rgba(168,85,247,0.14),transparent_35%),radial-gradient(circle_at_50%_100%,rgba(56,189,248,0.12),transparent_45%)]" />
        <div className="relative w-full max-w-3xl rounded-2xl border border-white/10 bg-slate-900/80 p-6 shadow-2xl backdrop-blur-md sm:p-10">
          <div className="mb-8 flex flex-col items-center gap-4 text-center">
            <img src={logoNameDark} alt="Verity AI" className="h-14 object-contain sm:h-16" />
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Offline Archive Viewer</h1>
              <p className="mx-auto max-w-2xl text-sm leading-6 text-white/75 sm:text-base">
                Open an engagement ZIP. Use this to view your archived engagements remotely.
              </p>
            </div>
          </div>

          {typeof sessionStorage !== "undefined" && sessionStorage.getItem("web_archive_mode") === "1" ? (
            <div className="mb-6 rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-left text-sm text-amber-100">
              After a full page refresh, choose the same ZIP again to restore engagement and UI data.
            </div>
          ) : null}

          <div className="rounded-xl border border-white/10 bg-slate-800/45 p-5 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-left">
                <p className="text-sm font-medium text-white/90">Engagement archive (.zip)</p>
                <p className="mt-1 text-xs text-white/55">Example: archive for Beta Test2</p>
              </div>
              <label className="inline-flex cursor-pointer items-center justify-center rounded-lg border border-blue-400/35 bg-blue-500/20 px-4 py-2 text-sm font-medium text-blue-100 transition hover:bg-blue-500/30">
                {isLoadingZip ? "Loading ZIP..." : "Choose .zip file"}
                <input
                  type="file"
                  accept=".zip,application/zip"
                  className="hidden"
                  disabled={isLoadingZip}
                  onChange={(e) => void onPickZip(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
            <div className="mt-4 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-left">
              <p className="text-xs uppercase tracking-wide text-white/45">Selected file</p>
              <p className="mt-1 truncate text-sm text-white/85">{selectedFileName}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
